import { useEffect, useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  useAltaRapida, useAprobadoresDeUsuario, useAreas, useCatalogos, useCrearUsuario,
  useGuardarAprobadoresDeUsuario, useGuardarUsuario, useRoles, useSegmentos, useSubirFoto,
  useUsuario, useUsuarios, type CuerpoDeUsuario,
} from './api';

/**
 * FormularioDeUsuario — el alta (completa y rápida) y la edición de una ficha.
 *
 * ## Lo que cambia
 *
 * - **Editar a alguien ya no depende de que esté entre los 200 primeros.** El
 *   vanilla pedía `/admin/users/?limit=200` y buscaba el id en la lista: con
 *   más de doscientos usuarios, el 201 respondía «Usuario no encontrado» y no
 *   había forma de llegar a él. El servidor no tenía ruta para una ficha
 *   suelta; se añadió (`GET /admin/users/{id}`), que es aditivo.
 * - **Los jefes suplentes se eligen de una lista con casillas**, no de un
 *   `<select multiple>` con «mantén pulsada Ctrl» escrito debajo.
 * - **La foto que no se pudo subir se dice una sola vez y con su motivo.**
 */
export function FormularioDeUsuario({
  id, rapida, onCerrar, onClave, onCompletar,
}: {
  id: number | null;
  /** Alta en dos etapas: solo lo mínimo, queda en preregistro. */
  rapida: boolean;
  onCerrar: () => void;
  onClave: (clave: string, titulo: string, nota: string) => void;
  onCompletar: (id: number) => void;
}) {
  const ficha = useUsuario(id);
  const areas = useAreas();
  const roles = useRoles();
  const catalogos = useCatalogos(!rapida);
  const todos = useUsuarios('', 'activos', !rapida && id != null);
  const suplentes = useAprobadoresDeUsuario(!rapida ? id : null);
  const segmentos = useSegmentos(!rapida ? id : null);

  const crear = useCrearUsuario();
  const alta = useAltaRapida();
  const guardar = useGuardarUsuario();
  const subirFoto = useSubirFoto();
  const guardarSuplentes = useGuardarAprobadoresDeUsuario();

  const [v, setV] = useState<CuerpoDeUsuario>({ role: 'colaborador' });
  const [foto, setFoto] = useState<File | null>(null);
  const [elegidos, setElegidos] = useState<number[]>([]);
  const [error, setError] = useState('');

  const u = ficha.data;
  const cat = catalogos.data;
  const esNuevo = id == null;

  useEffect(() => {
    if (!u) return;
    setV({
      full_name: u.full_name, email: u.email, cedula: u.cedula, role: u.role,
      area_id: u.area_id ?? null, position: u.position ?? '', phone: u.phone ?? '',
      city: u.city ?? '', company_id: u.company_id ?? null, sede_id: u.sede_id ?? null,
      position_id: u.position_id ?? null, gender: u.gender ?? '', status: u.status ?? '',
      work_city: u.work_city ?? '', manager_id: u.manager_id ?? null,
      contract_type_id: u.contract_type_id ?? null, cost_center: u.cost_center ?? '',
      birth_date: u.birth_date ?? '', hire_date: u.hire_date ?? '',
    });
  }, [u]);

  useEffect(() => {
    if (suplentes.data) setElegidos(suplentes.data.approvers.map((a) => a.id));
  }, [suplentes.data]);

  const poner = (parche: CuerpoDeUsuario) => { setV((x) => ({ ...x, ...parche })); setError(''); };
  const listaAreas = areas.data ?? [];
  const listaRoles = (roles.data ?? []).filter((r) => r.is_active !== false);

  function enviar() {
    if (rapida) {
      if (!v.cedula || !v.full_name || !v.email) {
        setError('Cédula, nombre y correo son obligatorios.'); return;
      }
      if (!v.area_id) { setError('Elige el área.'); return; }
      alta.mutate(
        { cedula: v.cedula, full_name: v.full_name, email: v.email, area_id: v.area_id, role: v.role },
        {
          onSuccess: (r) => {
            onCerrar();
            onClave(r.temp_password, 'Preregistro creado',
              'Comunícasela al colaborador. Queda en Preregistro: completa su información administrativa y actívalo.');
            onCompletar(r.id);
          },
          onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo crear'),
        },
      );
      return;
    }

    if (esNuevo) {
      if (!v.cedula || !v.full_name || !v.email) {
        setError('Cédula, nombre y correo son obligatorios.'); return;
      }
      if (!v.area_id) { setError('El área es obligatoria.'); return; }
      if (!v.birth_date) { setError('La fecha de nacimiento es obligatoria.'); return; }
      if (!v.hire_date) { setError('La fecha de ingreso es obligatoria.'); return; }
      crear.mutate(v, {
        onSuccess: async (r) => {
          if (foto && r.id) {
            try { await subirFoto.mutateAsync({ id: r.id, archivo: foto }); }
            catch { Utils.toast('El usuario se creó, pero la foto no se pudo subir', 'warning'); }
          }
          onCerrar();
          if (r.temp_password) {
            onClave(r.temp_password, 'Usuario creado',
              'Comunícasela al colaborador. Tendrá que cambiarla en su primer ingreso.');
          } else {
            Utils.toast('Usuario creado', 'success');
          }
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo crear'),
      });
      return;
    }

    guardar.mutate({ id, cuerpo: v }, {
      onSuccess: async () => {
        // El jefe inmediato no puede ser además suplente: el servidor lo
        // rechaza por duplicado, y quitarlo aquí evita perder el guardado
        // entero por un detalle obvio.
        const ids = elegidos.filter((x) => x !== v.manager_id);
        try { await guardarSuplentes.mutateAsync({ id, ids }); }
        catch (err) {
          Utils.toast(
            err instanceof Error ? `Ficha guardada, pero los suplentes no: ${err.message}`
              : 'Ficha guardada, pero los suplentes no', 'warning');
        }
        Utils.toast('Usuario actualizado', 'success');
        onCerrar();
      },
      onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo guardar'),
    });
  }

  const cargando = crear.isPending || alta.isPending || guardar.isPending;

  return (
    <Drawer
      abierto
      onCambio={(x) => { if (!x && !cargando) onCerrar(); }}
      titulo={rapida ? 'Alta rápida' : esNuevo ? 'Crear usuario' : 'Editar la ficha'}
      descripcion={rapida
        ? 'Registro básico: queda en Preregistro y después se completa y se activa.'
        : esNuevo
          ? 'La fecha de nacimiento y la de ingreso son obligatorias: de ahí salen las celebraciones y la antigüedad.'
          : u?.full_name}
      ancho={rapida ? 'md' : 'lg'}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={cargando}>
            <i className="bi bi-check-lg" aria-hidden="true" />{' '}
            {rapida ? 'Crear preregistro' : esNuevo ? 'Crear y enviar bienvenida' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      {!esNuevo && ficha.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : !esNuevo && ficha.isError ? (
        <EstadoError err={ficha.error} que="la ficha" onReintentar={() => void ficha.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          {error && <p role="alert" className="text-3 text-danger-ink">{error}</p>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Cédula">
              <Input
                value={String(v.cedula ?? '')}
                disabled={!esNuevo}
                onChange={(e) => poner({ cedula: e.target.value })}
              />
            </Field>
            <Field label="Nombre completo">
              <Input
                value={String(v.full_name ?? '')}
                onChange={(e) => poner({ full_name: e.target.value })}
              />
            </Field>
          </div>

          {esNuevo && (
            <Field label="Correo">
              <Input
                type="email"
                value={String(v.email ?? '')}
                onChange={(e) => poner({ email: e.target.value })}
              />
            </Field>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Área"
              ayuda={listaAreas.length ? undefined : 'No hay áreas: crea una antes.'}
            >
              <Select
                value={v.area_id == null ? '' : String(v.area_id)}
                onChange={(e) => poner({ area_id: Number(e.target.value) || null })}
              >
                <option value="">— Elegir —</option>
                {listaAreas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <Field label="Rol">
              <Select value={String(v.role ?? '')} onChange={(e) => poner({ role: e.target.value })}>
                {listaRoles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </Select>
            </Field>
          </div>

          {!rapida && (
            <>
              {esNuevo && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Fecha de nacimiento">
                    <Input
                      type="date"
                      value={String(v.birth_date ?? '')}
                      onChange={(e) => poner({ birth_date: e.target.value })}
                    />
                  </Field>
                  <Field label="Fecha de ingreso">
                    <Input
                      type="date"
                      value={String(v.hire_date ?? '')}
                      onChange={(e) => poner({ hire_date: e.target.value })}
                    />
                  </Field>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Empresa" opcional>
                  <Select
                    value={v.company_id == null ? '' : String(v.company_id)}
                    onChange={(e) => poner({ company_id: Number(e.target.value) || null })}
                  >
                    <option value="">—</option>
                    {(cat?.companies ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Sede" opcional>
                  <Select
                    value={v.sede_id == null ? '' : String(v.sede_id)}
                    onChange={(e) => poner({ sede_id: Number(e.target.value) || null })}
                  >
                    <option value="">—</option>
                    {(cat?.sedes ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Cargo (del catálogo)" opcional>
                  <Select
                    value={v.position_id == null ? '' : String(v.position_id)}
                    onChange={(e) => poner({ position_id: Number(e.target.value) || null })}
                  >
                    <option value="">—</option>
                    {(cat?.positions ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Cargo (texto libre)" opcional>
                  <Input
                    value={String(v.position ?? '')}
                    onChange={(e) => poner({ position: e.target.value })}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Teléfono o extensión" opcional>
                  <Input
                    value={String(v.phone ?? '')}
                    onChange={(e) => poner({ phone: e.target.value })}
                    placeholder="3001234567 o ext 1234"
                  />
                </Field>
                <Field label="Ciudad de residencia" opcional>
                  <Select
                    value={String(v.city ?? '')}
                    onChange={(e) => poner({ city: e.target.value })}
                  >
                    <option value="">—</option>
                    {(cat?.cities ?? []).map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </Select>
                </Field>
              </div>

              {esNuevo ? (
                <Field label="Foto de perfil" opcional ayuda="JPG o PNG.">
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    className="block w-full text-3 text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-brand file:px-3 file:py-2 file:text-3 file:font-semibold file:text-on-brand hover:file:bg-brand-strong"
                    onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
                  />
                </Field>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Género" opcional>
                      <Select
                        value={String(v.gender ?? '')}
                        onChange={(e) => poner({ gender: e.target.value })}
                      >
                        <option value="">— Sin definir —</option>
                        <option value="Masculino">Masculino</option>
                        <option value="Femenino">Femenino</option>
                      </Select>
                    </Field>
                    <Field
                      label="Estado"
                      opcional
                      ayuda="Vacío = se deduce de si la cuenta está activa."
                    >
                      <Select
                        value={String(v.status ?? '')}
                        onChange={(e) => poner({ status: e.target.value })}
                      >
                        <option value="">— Automático —</option>
                        <option value="Activo">Activo</option>
                        <option value="Retirado">Retirado</option>
                      </Select>
                    </Field>
                  </div>

                  <h3 className="flex items-center gap-2 border-t border-line pt-3 text-3 font-bold text-brand-ink">
                    <i className="bi bi-diagram-3" aria-hidden="true" /> Modelo organizacional
                  </h3>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Jefe inmediato" opcional>
                      <Select
                        value={v.manager_id == null ? '' : String(v.manager_id)}
                        onChange={(e) => poner({ manager_id: Number(e.target.value) || null })}
                      >
                        <option value="">— Sin jefe —</option>
                        {(todos.data?.items ?? [])
                          .filter((x) => x.id !== id)
                          .map((x) => <option key={x.id} value={x.id}>{x.full_name}</option>)}
                      </Select>
                    </Field>
                    <Field label="Tipo de contrato" opcional>
                      <Select
                        value={v.contract_type_id == null ? '' : String(v.contract_type_id)}
                        onChange={(e) => poner({ contract_type_id: Number(e.target.value) || null })}
                      >
                        <option value="">—</option>
                        {(cat?.contract_types ?? []).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <Field label="Centro de costos" opcional>
                    <Input
                      value={String(v.cost_center ?? '')}
                      onChange={(e) => poner({ cost_center: e.target.value })}
                      placeholder="Ej: CC-1001"
                    />
                  </Field>

                  <fieldset>
                    <legend className="text-3 font-semibold text-ink">
                      Jefes suplentes para aprobar solicitudes
                    </legend>
                    <p className="mb-2 text-2 text-muted">
                      Además del jefe inmediato. <strong>Basta que uno cualquiera apruebe</strong>:
                      sirve para que las solicitudes no se represen cuando el jefe está ausente.
                    </p>
                    <ul className="max-h-48 overflow-auto rounded-sm border border-line">
                      {(todos.data?.items ?? [])
                        .filter((x) => x.id !== id)
                        .map((x) => (
                          <li key={x.id} className="border-b border-line last:border-0">
                            <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-3 hover:bg-sunken">
                              <input
                                type="checkbox"
                                checked={elegidos.includes(x.id)}
                                onChange={(e) => setElegidos((s) => (e.target.checked
                                  ? [...s, x.id]
                                  : s.filter((y) => y !== x.id)))}
                              />
                              {x.full_name}
                            </label>
                          </li>
                        ))}
                    </ul>
                  </fieldset>

                  {!!segmentos.data?.tags?.length && (
                    <div>
                      <h3 className="mb-1.5 flex items-center gap-2 text-3 font-bold text-brand-ink">
                        <i className="bi bi-tags" aria-hidden="true" /> Etiquetas derivadas
                      </h3>
                      <ul className="flex flex-wrap gap-1.5">
                        {segmentos.data.tags.map((t) => (
                          <li key={t}><Badge tone="info">{t}</Badge></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
